import { useCallback, useEffect, useRef, useState } from 'react';
import { MediaFileType } from 'shared/enums/MediaFileType';
import { IProfileMediaSetSelectPayload } from 'shared/interfaces/IProfile';
import { profileService } from 'shared/services/profileService';
import { uploadService } from 'shared/services/uploadService';
import { EditProfileCustomSkeleton } from '../../EditProfile.styles';
import { EditProfileDocumentsVideoItemWrapper } from '../EditProfileMediaDocumentsVideo/EditProfileMediaDocumentVideoItem/EditProfileMediaDocumentVideoItem.styles';
import { Box, CircularProgress, CircularProgressProps, Snackbar, Typography } from '@material-ui/core';
import { useQueryClient } from 'react-query';
import { REPRESENTATIVES_QUERY_STRING } from 'shared/constants/REPRESENTATIVES_QUERY_STRING';
import { FileType } from 'shared/enums/FileType';
import { EUploadingStatus } from 'shared/enums/EUploadingStatus';
import { useSnackbar } from 'notistack';
import { MediaItemWrapper } from 'pages/Representatives/components/MediaItemCommon.styles';

type EditProfileMediaUploadingItemProps = {
  file: File;
  onDone: VoidFunction;
  mediaFileType: MediaFileType;
  profileId?: string;
};

const { setSelectProfileMedia } = profileService();
const { uploadMedia } = uploadService();

const fileTypeMap: any = {
  [MediaFileType.VIDEO]: FileType.VIDEO,
  [MediaFileType.AUDIO]: FileType.AUDIO,
  [MediaFileType.DOCUMENT]: FileType.DOCUMENT,
};

const EditProfileMediaUploadingItem = ({
  file,
  mediaFileType,
  onDone,
  profileId,
}: EditProfileMediaUploadingItemProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const { mutateAsync: uploadMediaMutate } = uploadMedia();
  const { mutateAsync: attachedToProfile } = setSelectProfileMedia();
  const [status, setStatus] = useState<EUploadingStatus>(EUploadingStatus.UPLOADING);
  const [progress, setProgress] = useState<number>(0);

  const handleSetProgress = useCallback((progress: number) => {
    setProgress(progress);
  }, []);

  const hasStarted = useRef(false);

  useEffect(() => {
    if (hasStarted.current) return;
    hasStarted.current = true;
    const handleUpload = async () => {
      const formData = new FormData();

      formData.set('attachment', file);
      formData.append('file_name', file.name);
      formData.append('file_type', mediaFileType);
      formData.set('file_size', file.size.toString());
      formData.append('media_bin', 'true');

      let uploadedFile;

      try {
        uploadedFile = await uploadMediaMutate(
          { formData, onProgres: handleSetProgress },
          {
            onSuccess: async () => {
              await queryClient.invalidateQueries(['media'], {
                refetchInactive: true,
              }),
                setStatus(EUploadingStatus.DONE);
            },
          },
        );
      } catch (error) {
        enqueueSnackbar(`${file.name} failed to upload`, {
          variant: 'error',
        });
        setStatus(EUploadingStatus.ERROR);
        onDone();
        return;
      }

      if (profileId) {
        const payload: IProfileMediaSetSelectPayload = {
          id: uploadedFile.data.id,
          tag_list: mediaFileType,
        };

        await attachedToProfile(
          {
            profileId,
            payload,
          },
          {
            onSuccess: async () => {
              await queryClient.invalidateQueries([
                REPRESENTATIVES_QUERY_STRING.representatives_single_profile_attached_media,
                profileId,
                fileTypeMap[mediaFileType],
              ]);
              onDone();
            },
            onError: async () => {
              setStatus(EUploadingStatus.ERROR_ATTACHED);
              enqueueSnackbar(`${file.name} failed to attached, but saved in media bin`, {
                variant: 'warning',
              });
              onDone();
              await queryClient.invalidateQueries([
                REPRESENTATIVES_QUERY_STRING.representatives_single_profile_hidden_media,
                profileId,
                fileTypeMap[mediaFileType],
              ]);
            },
          },
        );
      }
    };
    handleUpload();
  }, []);

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      {mediaFileType === MediaFileType.VIDEO ? (
        <EditProfileDocumentsVideoItemWrapper>
          <EditProfileCustomSkeleton width="100%" height="100%" variant="rect" />
        </EditProfileDocumentsVideoItemWrapper>
      ) : mediaFileType === MediaFileType.AUDIO ? (
        <MediaItemWrapper>
          <EditProfileCustomSkeleton width="100%" height="100%" variant="rect" />
        </MediaItemWrapper>
      ) : mediaFileType === MediaFileType.DOCUMENT ? (
        <MediaItemWrapper>
          <EditProfileCustomSkeleton width="100%" height="100%" variant="rect" />
        </MediaItemWrapper>
      ) : null}
      {status === EUploadingStatus.ERROR || status === EUploadingStatus.ERROR_ATTACHED ? null : (
        <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}>
          <CircularProgressWithLabel value={progress} />
        </div>
      )}
    </div>
  );
};

export default EditProfileMediaUploadingItem;

const CircularProgressWithLabel = (props: CircularProgressProps & { value: number }) => {
  return (
    <Box position="relative" display="inline-flex">
      <CircularProgress variant="determinate" {...props} />
      <Box
        top={0}
        left={0}
        bottom={0}
        right={0}
        position="absolute"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Typography
          variant="caption"
          style={{ fontSize: 10 }}
          component="div"
          color="textSecondary"
        >{`${Math.round(props.value)}%`}</Typography>
      </Box>
    </Box>
  );
};
